import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Plus, Check, Pencil, Trash2, SkipForward, Sparkles, ArrowRight,
} from 'lucide-react';
import FlowForm from './FlowForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-slate-100 text-slate-600',
};

const Chips = ({ values, labels, testId }) => (
  <div className="flex flex-wrap gap-1" data-testid={testId}>
    {(values || []).map((v) => (
      <span key={v} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
        {labels?.[v] || v}
      </span>
    ))}
  </div>
);

export const FlowMapper = ({ assessmentId, authHeader, onSkip, onCompleted, evidenceSkipped }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);     // flow_id of a confirmed flow
  const [confirming, setConfirming] = useState(null); // flow_id of a suggestion

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/evidence/${assessmentId}/flows`, authHeader());
      setData(res.data);
      return res.data;
    } catch (e) {
      toast.error('Could not load the flow map');
      return null;
    }
  }, [assessmentId, authHeader]);

  useEffect(() => {
    (async () => {
      const current = await load();
      if (current && !evidenceSkipped && current.suggestions.length === 0
          && current.flows.length === 0 && !current.locked) {
        try {
          const res = await axios.post(`${API}/evidence/${assessmentId}/flows/suggest`, {}, authHeader());
          setData(res.data);
        } catch (e) { /* suggestions are optional */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      setData(res.data);
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d?.code || 'Could not save the change');
      return false;
    } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="py-10 flex justify-center" data-testid="flow-mapper-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#003366]" />
      </div>
    );
  }

  const cat = data.catalogue;
  const confirmSuggestion = (flowId, patch = {}) => call(
    () => axios.post(`${API}/evidence/${assessmentId}/flows/${flowId}/confirm`, patch, authHeader()),
    'Flow confirmed',
  ).then((ok) => { if (ok) setConfirming(null); });

  return (
    <div className="space-y-6" data-testid="flow-mapper">
      <p className="text-base text-slate-600 max-w-3xl">
        A data flow is one place personal data sits or travels. We have pre-filled what the
        evidence already shows — confirm, edit or remove each card, then add anything we missed.
        This takes two to three minutes and never changes your score.
      </p>

      {/* Suggested cards */}
      {data.suggestions.length > 0 && (
        <div className="space-y-3" data-testid="flow-suggestions-list">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#008080]" />
            <h3 className="text-sm font-[700] text-[#003366]">
              From your evidence — {data.suggestions.length} to confirm
            </h3>
          </div>
          {data.suggestions.map((s) => (
            <div key={s.flow_id} className="border border-[#008080]/40 bg-[#008080]/5 rounded-sm p-4 space-y-2"
              data-testid={`flow-suggestion-${s.flow_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[#008080] text-white">Suggested – please confirm</Badge>
                <span className="text-sm font-[700] text-[#003366]">{s.system_name}</span>
                <span className="text-xs text-slate-500">
                  {cat.system_types[s.system_type]} · {cat.roles[s.role]}
                </span>
              </div>
              <div className="text-sm text-slate-600">{s.evidence_text}</div>
              <Chips values={s.data_categories} labels={cat.data_categories}
                testId={`flow-suggestion-cats-${s.flow_id}`} />
              {confirming === s.flow_id ? (
                <div className="bg-white border border-slate-200 rounded-sm p-4 mt-2">
                  <FlowForm catalogue={cat} initial={s} busy={busy}
                    idPrefix={`flow-edit-suggestion-${s.flow_id}`}
                    onSave={(payload) => confirmSuggestion(s.flow_id, payload)}
                    onCancel={() => setConfirming(null)} />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={() => confirmSuggestion(s.flow_id)} disabled={busy}
                    className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                    data-testid={`flow-confirm-btn-${s.flow_id}`}>
                    <Check className="h-4 w-4 mr-1" /> Confirm
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirming(s.flow_id)}
                    disabled={busy} className="rounded-sm" data-testid={`flow-edit-suggestion-btn-${s.flow_id}`}>
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => call(
                    () => axios.post(`${API}/evidence/${assessmentId}/flows/${s.flow_id}/dismiss`, {}, authHeader()),
                    'Suggestion dismissed')} disabled={busy}
                    className="text-slate-500" data-testid={`flow-dismiss-btn-${s.flow_id}`}>
                    Dismiss
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirmed flows */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-[700] text-[#003366]" data-testid="flow-confirmed-heading">
            Your data flows — {data.counts.confirmed} confirmed
          </h3>
          {data.counts.high_severity_gaps > 0 && (
            <Badge className="bg-red-100 text-red-700" data-testid="flow-high-gap-count">
              {data.counts.high_severity_gaps} high-priority gap(s)
            </Badge>
          )}
        </div>

        {data.flows.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm p-4"
            data-testid="flow-empty-state">
            No flows recorded yet. Confirm a suggestion above or add a system below.
          </div>
        )}

        {data.flows.map((f) => (
          <div key={f.flow_id} className="border border-slate-200 rounded-sm p-4 space-y-2"
            data-testid={`flow-row-${f.flow_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-[700] text-slate-800">{f.system_name}</span>
              <span className="text-xs text-slate-500">
                {cat.system_types[f.system_type]} · {cat.roles[f.role]} · {cat.directions[f.direction]}
              </span>
              <Badge className="bg-slate-100 text-slate-600 ml-auto">{cat.sources[f.source]}</Badge>
            </div>
            <Chips values={f.data_categories} labels={cat.data_categories} testId={`flow-cats-${f.flow_id}`} />
            {(f.dpdp_refs || []).length > 0 && (
              <div className="text-xs text-slate-500" data-testid={`flow-refs-${f.flow_id}`}>
                DPDP: {f.dpdp_refs.join(' · ')}
              </div>
            )}
            {(f.flow_gaps || []).length > 0 && (
              <div className="space-y-1" data-testid={`flow-gaps-${f.flow_id}`}>
                {f.flow_gaps.map((g) => (
                  <div key={g.code} className="flex items-start gap-2">
                    <Badge className={SEVERITY_STYLES[g.severity] || SEVERITY_STYLES.low}>
                      {g.severity}
                    </Badge>
                    <span className="text-xs text-slate-600">{g.message}</span>
                  </div>
                ))}
              </div>
            )}
            {editing === f.flow_id ? (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                <FlowForm catalogue={cat} initial={f} busy={busy} idPrefix={`flow-edit-${f.flow_id}`}
                  onSave={(payload) => call(
                    () => axios.patch(`${API}/evidence/${assessmentId}/flows/${f.flow_id}`, payload, authHeader()),
                    'Flow updated').then((ok) => { if (ok) setEditing(null); })}
                  onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(f.flow_id)} disabled={busy}
                  className="rounded-sm" data-testid={`flow-edit-btn-${f.flow_id}`}>
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => call(
                  () => axios.delete(`${API}/evidence/${assessmentId}/flows/${f.flow_id}`, authHeader()),
                  'Flow removed')} disabled={busy} className="text-red-600"
                  data-testid={`flow-delete-btn-${f.flow_id}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Manual add */}
      {adding ? (
        <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="flow-add-block">
          <FlowForm catalogue={cat} busy={busy} idPrefix="flow-add"
            onSave={(payload) => call(
              () => axios.post(`${API}/evidence/${assessmentId}/flows`,
                { ...payload, source: 'manual' }, authHeader()),
              'Flow added').then((ok) => { if (ok) setAdding(false); })}
            onCancel={() => setAdding(false)} />
        </div>
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)} disabled={busy || data.locked}
          className="rounded-sm" data-testid="flow-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add a system or party
        </Button>
      )}

      <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <Button onClick={() => call(
          () => axios.post(`${API}/evidence/${assessmentId}/flows/complete`, {}, authHeader()),
          'Flow map saved').then((ok) => { if (ok && onCompleted) onCompleted(); })}
          disabled={busy} className="bg-[#003366] hover:bg-[#002244] rounded-sm"
          data-testid="flow-complete-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
          Done — continue
        </Button>
        <Button variant="outline" onClick={onSkip} disabled={busy} className="rounded-sm"
          data-testid="evidence-skip-systems-btn">
          <SkipForward className="h-4 w-4 mr-2" /> Skip this step
        </Button>
      </div>
    </div>
  );
};

export default FlowMapper;
